import axios, { AxiosPromise, AxiosInstance, Method } from 'axios';
import { LOGIN_ROUTER_PATH } from '@/constants/router-path';
import setAlertValue from '@/components/alert';
import { QUERY_REDIRECT } from '@/constants/auth';

const mockUrls: Array<string> = ['/users'];

class AxiosClient {
  instance: AxiosInstance;
  mockInstance: AxiosInstance;

  constructor() {
    this.instance = axios.create();
    this.mockInstance = axios.create();
    this.setDefaultOption(this.instance);
    this.setDefaultOption(this.mockInstance);
    this.setInterceptors(this.instance);
    this.setInterceptors(this.mockInstance);
  }

  setDefaultOption(instance: AxiosInstance) {
    instance.defaults.timeout = 30 * 1000;
    instance.defaults.headers.post['Content-Type'] = 'application/json';
    instance.defaults.headers.put['Content-Type'] = 'application/json';
    instance.defaults.baseURL = '/';
    instance.defaults.withCredentials = true; // 跨域携带cookie
    instance.defaults.xsrfCookieName = 'csrftoken';
    instance.defaults.xsrfHeaderName = 'X-CSRFToken';
  }
  setInterceptors(instance: AxiosInstance) {
    instance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        const { response } = error;
        if (response) {
          const { status, data } = response;
          const message = data?.detail || data?.message;
          switch (status) {
            case 403:
              if (
                message.includes('Authentication credentials were not provided')
              ) {
                setAlertValue({ msg: '登录已失效，请重新登录！' });
                window.location.href = `${LOGIN_ROUTER_PATH}?${QUERY_REDIRECT}=${window.location.pathname + window.location.search}`;
              }
              break;
          }
        }
        return Promise.reject(error);
      },
    );
  }

  isMock(url: string): boolean {
    return mockUrls.includes(url) || url.startsWith('/mock');
  }

  request(
    url: string,
    data = {},
    method: Method = 'get',
    options = {},
  ): AxiosPromise {
    const isGet = method === 'get';
    const isDelete = method === 'delete';
    const instance = this.instance;

    let params = {};
    if (isGet || isDelete) {
      params = data;
      data = {};
    }
    return instance({ method, url, data, params, ...options });
  }

  get(
    url: string,
    params?: Record<string, unknown>,
    options?: Record<string, unknown>,
  ): AxiosPromise {
    return this.request(url, params, 'get', options);
  }

  post(
    url: string,
    data?: FormData | Record<string, unknown>,
    options?: Record<string, unknown>,
  ): AxiosPromise {
    return this.request(url, data, 'post', options);
  }

  put(
    url: string,
    data?: Record<string, unknown>,
    options?: Record<string, unknown>,
  ): AxiosPromise {
    return this.request(url, data, 'put', options);
  }

  patch(
    url: string,
    data?: Record<string, unknown>,
    options?: Record<string, unknown>,
  ): AxiosPromise {
    return this.request(url, data, 'patch', options);
  }

  delete(
    url: string,
    params?: Record<string, unknown>,
    options?: Record<string, unknown>,
  ): AxiosPromise {
    return this.request(url, params, 'delete', options);
  }
}

const client = new AxiosClient();
export default client;
